// The node view of a GFM table: the same skeleton as TipTap's TableView (plain
// DOM with a colgroup), plus the overlay of controls (handles, strips, grips)
// rendered by a ReactRenderer.
//
// Not a React node view (`ReactNodeViewRenderer`): nothing can come before
// `NodeViewContent` there, because `@tiptap/react` appends the contentDOM again
// unless it is the first child of its holder. A table needs `colgroup` before
// `tbody` (the contentDOM) for column widths to apply.
//
// DOM:
//   div.trameria-table                        <- dom (data-block-id here), position: relative.
//                                                The handle gutter (top 12 / left 16) and the
//                                                "+" strips (bottom 20 / right 16) are its
//                                                padding; negative margins line it up with the body.
//     div.trameria-table-scroll               <- the scroll container: no padding, no margin,
//                                                the same box as the table (its scrollbars
//                                                run along the table's edge)
//       table[style=table-layout:fixed;width]  <- never narrower than the body (CSS min-width)
//         colgroup > col[style=width:Nem]
//         tbody                               <- contentDOM
//     div.trameria-table-overlay              <- the ReactRenderer (TableOverlay): the
//                                                container's sibling, laid over its box
//
// The overlay is outside the container because with the gutter inside it, the
// scrollbars stuck out past the table's edge by the gutter (12px up, 16px
// left). The overlay does not scroll, so its content subtracts `scroll`.
//
// A read-only editor gets no overlay: no DOM per row and no
// `getBoundingClientRect()` for a screen that cannot press anything. Decided
// once in the constructor; the frozen row / column variables are set anyway.
import type { Editor, NodeViewRendererProps } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { ReactRenderer } from "@tiptap/react";
import {
  BLOCK_ID_DOM_ATTR,
  blockIdDomAttrs,
  TrameriaTable,
} from "@trameria/editor";
import { canEdit } from "./editor-steps";
import {
  TableOverlay,
  type TableOverlayProps,
  type TableRects,
} from "./table-overlay";

export class TableNodeView {
  dom: HTMLDivElement;
  contentDOM: HTMLTableSectionElement;
  node: PMNode;
  private editor: Editor;
  private getPos: NodeViewRendererProps["getPos"];
  private scroll: HTMLDivElement;
  private table: HTMLTableElement;
  private colgroup: HTMLTableColElement;
  /** Not made for a read-only editor (no DOM, no measuring). */
  private overlay: ReactRenderer<unknown, TableOverlayProps> | null = null;
  private observer: ResizeObserver | null = null;
  private frame: number | null = null;
  /** Checked when `document.fonts.ready` resolves after this view went away. */
  private destroyed = false;
  /** Scroll positions go to the overlay once per frame. */
  private scrollFrame: number | null = null;
  /** Widths shown while dragging: kept so a sync arriving meanwhile does not undo them. */
  private preview: number[] | null = null;
  private rects: TableRects = {
    rows: [],
    cols: [],
    table: emptyRect(),
  };
  private fontSizePx = 16;

  constructor(props: NodeViewRendererProps) {
    this.node = props.node;
    this.editor = props.editor as Editor;
    this.getPos = props.getPos;
    this.dom = document.createElement("div");
    this.dom.className = "trameria-table";
    this.scroll = document.createElement("div");
    this.scroll.className = "trameria-table-scroll";
    this.table = document.createElement("table");
    this.colgroup = document.createElement("colgroup");
    this.contentDOM = document.createElement("tbody");
    this.table.append(this.colgroup, this.contentDOM);
    this.scroll.append(this.table);
    this.dom.append(this.scroll);
    this.applyAttrs();
    // Whether it can be written is checked once: node views are not rebuilt,
    // so a later `setEditable(true)` would not add the overlay (nothing calls
    // that today). The phone's reading state is only a pause, so it counts as
    // writable.
    if (canEdit(this.editor)) {
      this.overlay = new ReactRenderer(TableOverlay, {
        editor: this.editor,
        className: "trameria-table-overlay",
        props: this.overlayProps(),
      });
      // Inside the contenteditable body: neither PM nor the IME may touch it.
      this.overlay.element.contentEditable = "false";
      // The container's sibling (outside the scrolling box): see the top.
      this.dom.append(this.overlay.element);
      if (typeof ResizeObserver !== "undefined") {
        // Fonts arrive late, so measuring once on mount is not enough: measure
        // whenever the table's size changes (wrapping and scrolling are left
        // to CSS). The container too: max-height and scrollbars appearing
        // change its visible size.
        this.observer = new ResizeObserver(() => this.measure());
        this.observer.observe(this.table);
        this.observer.observe(this.scroll);
      }
      this.scroll.addEventListener("scroll", this.onScroll, { passive: true });
    }
    // The table's size changes when fonts arrive. The ResizeObserver catches
    // it, but fonts already loaded may change nothing it sees; one more
    // measure covers that - the second frozen row's offset depends on the
    // first row's height, and a stale value leaves a gap.
    document.fonts?.ready
      .then(() => {
        if (this.destroyed) return;
        this.scheduleMeasure();
      })
      .catch(() => {});
    // Without an overlay, the frozen row / column variables are still set.
    this.scheduleMeasure();
  }

  /**
   * Hands the container's scroll position to the overlay, once per frame (a
   * single swipe fires scroll dozens of times). The overlay is outside the
   * container and does not move with it, so handles and grips follow this
   * value (frozen rows / columns stay; what slid under them is hidden).
   */
  private onScroll = () => {
    if (this.scrollFrame !== null) return;
    const run = () => {
      this.scrollFrame = null;
      this.overlay?.updateProps({
        scroll: { top: this.scroll.scrollTop, left: this.scroll.scrollLeft },
      });
    };
    if (typeof requestAnimationFrame !== "function") {
      run();
      return;
    }
    this.scrollFrame = requestAnimationFrame(run);
  };

  /**
   * Measures in the next frame (once however often it is asked). ProseMirror
   * replaces the contentDOM's children after `update()` returned true, so
   * measuring inside `update()` reads the old rows. Changes that keep the
   * table's box (attributes only, a column added without width change, the
   * automatic layout redistributing) do not fire the ResizeObserver, so this
   * is their only trigger.
   */
  private scheduleMeasure() {
    if (this.frame !== null) return;
    if (typeof requestAnimationFrame !== "function") {
      queueMicrotask(() => this.measure());
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.measure();
    });
  }

  /** Copies the attributes (widths / fixRows / fixCols / blockId) to the DOM. */
  private applyAttrs() {
    const widths = this.node.attrs.widths as number[] | null;
    this.colgroup.replaceChildren();
    if (widths && widths.length > 0) {
      for (const w of widths) {
        const col = document.createElement("col");
        col.style.width = `${w}em`;
        this.colgroup.append(col);
      }
      this.table.style.tableLayout = "fixed";
      this.table.style.width = `${widths.reduce((a, b) => a + b, 0)}em`;
    } else {
      this.table.style.tableLayout = "";
      this.table.style.width = "";
    }
    setFix(this.scroll, "fixRows", this.node.attrs.fixRows);
    setFix(this.scroll, "fixCols", this.node.attrs.fixCols);
    // A node view's DOM does not go through renderHTML, so the block id is set
    // here (`blockIdDomAttrs` owns the attribute name and "none until assigned").
    const blockId = blockIdDomAttrs(this.node)[BLOCK_ID_DOM_ATTR];
    if (blockId) this.dom.setAttribute(BLOCK_ID_DOM_ATTR, blockId);
    else this.dom.removeAttribute(BLOCK_ID_DOM_ATTR);
  }

  /**
   * Measures rows and columns and hands them to the overlay; also sets where
   * the second row / column sticks (the first row's height / first column's
   * width) as CSS variables on the container.
   *
   * One header cell = one column (GFM tables have no merged cells; tables
   * pasted as HTML may, and then the controls do not line up).
   */
  private measure() {
    const rows = Array.from(this.contentDOM.children) as HTMLElement[];
    const cells = rows[0]
      ? (Array.from(rows[0].children) as HTMLElement[])
      : [];
    const overlay = this.overlay;
    if (!overlay) {
      // A read-only table still gets the variables, so frozen rows / columns
      // do not overlap. Only two rectangles are measured. Known limit: without
      // an overlay there is no ResizeObserver, so late fonts do not update them.
      this.setFixVars(
        rows[0]?.getBoundingClientRect().height ?? 0,
        cells[0]?.getBoundingClientRect().width ?? 0,
      );
      return;
    }
    const table = this.table.getBoundingClientRect();
    this.rects = {
      // Rows (`<tr>`) never stick (cells do), so their rectangles are usable as is.
      rows: rows.map((r) => r.getBoundingClientRect()),
      // Columns are built from widths added up. Frozen columns are
      // `position: sticky`, so measured while scrolled sideways their `left`
      // is the stuck position and "distance from the table's left" goes wrong
      // (measured: at scrollLeft 260 the first border was off by 260px).
      // Widths do not change when stuck, so adding them from the table's left
      // is independent of scrolling.
      cols: cumulativeCols(cells, table),
      table,
    };
    this.fontSizePx =
      parseFloat(getComputedStyle(this.table).fontSize || "") || 16;
    this.setFixVars(
      this.rects.rows[0]?.height ?? 0,
      this.rects.cols[0]?.width ?? 0,
    );
    // The overlay lies exactly over the container's box (which has no gutter,
    // so its top left = the table's resting top left = the origin of `rects`).
    // It does not scroll.
    const element = overlay.element;
    element.style.top = `${this.scroll.offsetTop}px`;
    element.style.left = `${this.scroll.offsetLeft}px`;
    element.style.width = `${this.scroll.offsetWidth}px`;
    element.style.height = `${this.scroll.offsetHeight}px`;
    overlay.updateProps({
      rects: this.rects,
      fontSizePx: this.fontSizePx,
      viewport: {
        clientWidth: this.scroll.clientWidth,
        clientHeight: this.scroll.clientHeight,
        offsetWidth: this.scroll.offsetWidth,
        offsetHeight: this.scroll.offsetHeight,
      },
    });
    // `ReactRenderer` draws only into `editor.contentComponent` (the portal
    // holder EditorContent makes); `render()` before it exists does nothing.
    // Node views can be made before EditorContent's effect, so render again
    // every time (idempotent).
    overlay.render();
  }

  /** Where the second row / column sticks. The CSS reads these. */
  private setFixVars(rowHeight: number, colWidth: number) {
    this.scroll.style.setProperty("--trameria-fix-row-1", `${rowHeight}px`);
    this.scroll.style.setProperty("--trameria-fix-col-1", `${colWidth}px`);
  }

  private overlayProps(): TableOverlayProps {
    return {
      editor: this.editor,
      getPos: this.getPos,
      node: this.node,
      rects: this.rects,
      fontSizePx: this.fontSizePx,
      onResize: (widths) => this.writeWidths(widths),
      onPreviewWidths: (widths) => this.previewWidths(widths),
      onPreviewSlack: (px) => this.previewSlack(px),
      onAutoScroll: (dx) => this.autoScroll(dx),
      onMenuOpenChange: (open) => this.setMenuOpen(open),
    };
  }

  /**
   * Marks the table while its menu is open; the CSS raises its overlay
   * (`.trameria-table[data-menu-open] .trameria-table-overlay`). With two
   * tables, the later one's overlay covered the earlier one's menu.
   */
  setMenuOpen(open: boolean) {
    if (open) this.dom.dataset.menuOpen = "true";
    else delete this.dom.dataset.menuOpen;
  }

  /** The widths on release: the only write to the document. */
  writeWidths(widths: number[]) {
    const pos = this.getPos();
    if (pos === undefined) return;
    const { state, view } = this.editor;
    const node = state.doc.nodeAt(pos);
    if (!node || node.type !== this.node.type) return;
    view.dispatch(
      state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, widths }),
    );
  }

  /**
   * Widths shown while dragging, put into the colgroup directly (the document
   * is not touched: no Yjs round trip per pixel). Someone else's edit arriving
   * meanwhile calls `update()` -> `applyAttrs()`, which would write the stored
   * widths back, so they are kept in `preview` and applied again.
   */
  previewWidths(widths: number[] | null) {
    this.preview = widths;
    if (!widths) {
      this.applyAttrs();
      return;
    }
    this.applyPreview();
  }

  /**
   * Auto-scroll while a grip is held: adds `dx` to the container and returns
   * the actual `scrollLeft` (so the overlay sees when it stopped at an end).
   * `dx` 0 only reads.
   */
  autoScroll(dx: number): number {
    if (dx !== 0) this.scroll.scrollLeft += dx;
    return this.scroll.scrollLeft;
  }

  /**
   * Slack (px) on the container's right for what the table shrank while
   * dragging; null eases it back to 0 over 180ms. The document is not touched.
   * Shrinking while scrolled to the far right also shrinks the scroll range,
   * the browser cuts `scrollLeft` and the content slides right (the line stays
   * at the edge while the pointer goes left). Padding keeps the range, so the
   * line follows. On release it eases back: as the range shrinks the browser
   * cuts `scrollLeft` gradually, so the content glides instead of jumping
   * (`scrollLeft` is never set here).
   */
  previewSlack(px: number | null) {
    const el = this.scroll;
    if (px !== null) {
      el.style.transition = "";
      el.style.paddingRight = `${px}px`;
      return;
    }
    // Nothing to ease back.
    if (!el.style.paddingRight || el.style.paddingRight === "0px") {
      el.style.paddingRight = "";
      el.style.transition = "";
      return;
    }
    el.style.transition = "padding-right 180ms ease";
    el.style.paddingRight = "0px";
    const done = (event: Event) => {
      // Other properties' transitions are ignored (happy-dom has no
      // TransitionEvent, so `propertyName` is looked at, not the type).
      const property = (event as TransitionEvent).propertyName;
      if (property && property !== "padding-right") return;
      el.removeEventListener("transitionend", done);
      el.style.transition = "";
      el.style.paddingRight = "";
    };
    el.addEventListener("transitionend", done);
  }

  /**
   * Writes the dragged widths to the DOM: the colgroup and the table's width.
   * Trading width with a neighbour keeps the total, but the last grip and a
   * table wider than the body change it, and with `table-layout: fixed` the
   * used width is the larger of the given width and the columns' sum: leaving
   * the width as it was hid shrinking until release, then the table jumped. A
   * table without widths has an empty colgroup, so it is made here
   * (`applyAttrs()` restores it).
   */
  private applyPreview() {
    const widths = this.preview;
    if (!widths) return;
    const cols = Array.from(this.colgroup.children) as HTMLElement[];
    if (cols.length !== widths.length) {
      this.colgroup.replaceChildren();
      for (const w of widths) {
        const col = document.createElement("col");
        col.style.width = `${w}em`;
        this.colgroup.append(col);
      }
    } else {
      widths.forEach((w, i) => {
        const col = cols[i];
        if (col) col.style.width = `${w}em`;
      });
    }
    this.table.style.tableLayout = "fixed";
    this.table.style.width = `${widths.reduce((a, b) => a + b, 0)}em`;
  }

  update(node: PMNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.applyAttrs();
    // While held, show the local widths, not the ones a sync wrote back.
    this.applyPreview();
    this.overlay?.updateProps({ node });
    // PM replaces the contentDOM after this: measure in the next frame.
    this.scheduleMeasure();
    return true;
  }

  /** The overlay, the colgroup and attributes set here are not for PM to read back. */
  ignoreMutation(
    mutation: MutationRecord | { type: "selection"; target: Node },
  ) {
    const target = mutation.target;
    if (this.overlay?.element.contains(target)) return true;
    if (this.colgroup.contains(target)) return true;
    return (
      mutation.type === "attributes" &&
      (target === this.dom || target === this.scroll || target === this.table)
    );
  }

  /** What happens in the overlay is not an edit of the body. */
  stopEvent(event: Event) {
    const target = event.target;
    return target instanceof Node && !!this.overlay?.element.contains(target);
  }

  destroy() {
    this.destroyed = true;
    this.scroll.removeEventListener("scroll", this.onScroll);
    if (
      this.scrollFrame !== null &&
      typeof cancelAnimationFrame === "function"
    ) {
      cancelAnimationFrame(this.scrollFrame);
    }
    this.scrollFrame = null;
    this.observer?.disconnect();
    this.observer = null;
    if (this.frame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.frame);
    }
    this.frame = null;
    this.overlay?.destroy();
    this.overlay = null;
  }
}

/**
 * Column rectangles from the header cells' widths added up from the table's
 * left edge (one cell = one column; GFM tables have no merged cells).
 */
function cumulativeCols(cells: HTMLElement[], table: DOMRect): DOMRect[] {
  let left = table.left;
  return cells.map((cell) => {
    const r = cell.getBoundingClientRect();
    const rect = {
      x: left,
      y: table.top,
      left,
      top: table.top,
      right: left + r.width,
      bottom: table.top + r.height,
      width: r.width,
      height: r.height,
      toJSON: () => ({}),
    } as DOMRect;
    left += r.width;
    return rect;
  });
}

function emptyRect(): DOMRect {
  return {
    x: 0,
    y: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
    toJSON: () => ({}),
  };
}

/** 0 (the default) removes the attribute; the CSS also looks at its presence. */
function setFix(el: HTMLElement, key: "fixRows" | "fixCols", value: unknown) {
  const n = Number(value ?? 0);
  if (n === 1 || n === 2) el.dataset[key] = String(n);
  else delete el.dataset[key];
}

/**
 * The editor's table with this node view. Same name, so it replaces the
 * built-in one through `createEditorExtensions({ extensions })`; the markdown
 * mapping (option line included) is inherited.
 */
export const TableWithControls = TrameriaTable.extend({
  addNodeView() {
    return (props) => new TableNodeView(props);
  },
});
